import clsx from 'clsx';
import type React from 'react';
import type { PressEvent } from 'react-aria';

import type * as api from '@gitbook/api-client';
import type { RevisionPage } from '@gitbook/hive-core';
import { Icon } from '@gitbook/spine-icons';
import { AtomButton, Button, renderIcon, Tooltip, type TooltipProps } from '@gitbook/spine-react';

import { PageIcon } from '../Page';

const PAGE_ICON_SIZE = 14;

/**
 * The context a sent message carried, shown under it: what was selected, and the comments it was
 * grounded on. The page a conversation is working in belongs to the surface rather than to a single
 * message, which is why {@link PageIndicator} is offered separately.
 */
export function EditorContextSummary(props: {
    selectedText?: string;
    comments?: api.AIMessageContext['comments'];
    className?: string;
}) {
    const { className, selectedText, comments } = props;

    return (
        <div className={clsx('flex min-w-0 flex-wrap gap-1.5', className)}>
            <SelectionIndicator selectedText={selectedText} />
            {comments?.map((comment) => (
                <CommentIndicator key={comment.id} comment={comment} />
            ))}
        </div>
    );
}

function CommentIndicator(props: { comment: NonNullable<api.AIMessageContext['comments']>[0] }) {
    const { comment } = props;

    return (
        <Indicator
            label={comment.content || 'Comment'}
            icon={renderIcon(Icon.CommentThread, { className: 'size-3.5 text-secondary' })}
            tooltip={{ label: 'Comment included as context' }}
        />
    );
}

export function PageIndicator(props: {
    page?: RevisionPage | null;
    isHidden?: boolean;
    onToggle?: () => void;
    fill?: boolean;
    className?: string;
}) {
    const { page, isHidden, onToggle, fill, className } = props;

    if (!page) {
        return null;
    }

    return (
        <Indicator
            label={page.title}
            icon={
                isHidden ? (
                    renderIcon(Icon.EyeOff, { className: 'size-3.5 text-neutral-low' })
                ) : (
                    // Low colour whatever the page's icon is — an emoji ignores it, a glyph takes it.
                    <PageIcon page={page} size={PAGE_ICON_SIZE} className="text-neutral-low" />
                )
            }
            tooltip={
                isHidden
                    ? {
                          label: 'Current page is hidden from the agent.',
                          description: 'Click to use it as context again.',
                      }
                    : {
                          label: 'Current page is used as context.',
                          description: 'Click to hide it from the agent.',
                      }
            }
            muted={isHidden}
            onPress={onToggle}
            fill={fill}
            className={className}
        />
    );
}

/**
 * The text the user has selected in the editor.
 *
 * Offers either of two ways out, never both: `onRemove` drops the selection itself (and clears it in
 * the editor), while `onToggle` only takes it out of the context and can put it back — the same
 * hide/show the page indicator does.
 */
export function SelectionIndicator(props: {
    selectedText?: string;
    onRemove?: () => void;
    isHidden?: boolean;
    onToggle?: () => void;
    fill?: boolean;
    className?: string;
}) {
    const { selectedText, onRemove, isHidden, onToggle, fill, className } = props;

    if (!selectedText) {
        return null;
    }

    return (
        <Indicator
            label={selectedText}
            icon={renderIcon(isHidden ? Icon.EyeOff : Icon.SelectedText, {
                className: 'size-3.5 text-neutral-low',
            })}
            tooltip={
                onToggle
                    ? isHidden
                        ? {
                              label: 'Selected text is hidden from the agent.',
                              description: 'Click to use it as context again.',
                          }
                        : {
                              label: 'Selected text is used as context.',
                              description: 'Click to hide it from the agent.',
                          }
                    : { label: 'Selection included as context' }
            }
            muted={isHidden}
            onPress={onToggle}
            fill={fill}
            className={className}
            action={
                onRemove ? (
                    <Button
                        kind="blank"
                        size="small"
                        iconOnly
                        label="Remove"
                        leadingIcon={Icon.ClearCircle}
                        onPress={onRemove}
                    />
                ) : null
            }
        />
    );
}

/**
 * Compact pill showing a piece of agent context: the current page, a selection,
 * or an attachment. The whole pill can be pressable (attachments open on click)
 * and can carry a trailing action (e.g. remove) revealed on hover.
 */
export function Indicator(props: {
    label: string;
    icon: React.ReactNode;
    tooltip?: Pick<TooltipProps, 'label' | 'description'>;
    action?: React.ReactNode;
    onPress?: (event: PressEvent) => void;
    /** Dim the pill to signal it is excluded from the agent context. */
    muted?: boolean;
    /**
     * Share the row with its siblings instead of taking the default width cap: the pill shrinks and
     * truncates as the row runs out of space. For a row that must stay on one line.
     */
    fill?: boolean;
    /** Extra classes on the pill, e.g. another fill when the surface behind is the pill's own. */
    className?: string;
}) {
    const { label, icon, tooltip, action, onPress, muted, fill, className } = props;

    const content = (
        <div className="flex w-full items-center gap-1 overflow-hidden text-ui-small">
            <span className="flex shrink-0 items-center">{icon}</span>
            <span className="w-full grow truncate">{label}</span>
        </div>
    );

    const pill = (
        <div
            className={clsx(
                // Narrower and tighter than a standalone chip: several of these sit above a
                // composer that is itself narrow, and they wrap rather than scroll, so every row
                // they take is a row of the conversation lost.
                'group relative flex min-w-0 items-center rounded-md border border-neutral-subtle bg-neutral-ui px-1.5 py-0.5 text-ui-standout font-[440] text-neutral-high transition-opacity',
                fill ? 'shrink' : 'max-w-40',
                onPress ? 'cursor-pointer' : 'cursor-default',
                muted && 'opacity-50',
                className
            )}
        >
            {onPress ? <AtomButton onPress={onPress}>{content}</AtomButton> : content}
            {action ? (
                <div className="absolute inset-y-0 right-0 flex items-center rounded-r-md bg-neutral-ui pl-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                    {action}
                </div>
            ) : null}
        </div>
    );

    if (tooltip) {
        return <Tooltip {...tooltip}>{pill}</Tooltip>;
    }

    return pill;
}
